/**
 * AI Scribe Recordings Service
 *
 * Public API for the recordings data layer.
 */

// Types
export type {
  RecordingStatus,
  RecordingProcessingStage,
  TranscriptionQuality,
  ActionItemAssignee,
  RecordingMode,
  RecordingMeetingType,
  RecordingMeetingTypeClassifiedBy,
  RecordingMatterBindSource,
  MatchConfidence,
  AudioFormat,
  RecordingParticipant,
  RecordingCreatedBy,
  RecordingImportStats,
  Recording,
  // VALE-986 — the enquiry a recording was captured for
  RecordingLeadContext,
  TranscriptSegment,
  RecordingTranscript,
  NoteSectionFormat,
  RecordingAiSummarySection,
  RecordingAiSummary,
  ConfidenceLevel,
  InstructionType,
  LegalPersonalRepresentativeRoleSlug,
  AiScribeExtractedContact,
  AiScribeExtractedInventoryItem,
  AiScribeExtractedChecklistItem,
  AiScribeExistingItemNote,
  AiScribeExtractedDeceasedAddress,
  AiScribeExtractedDeceased,
  RecordingAiExtractedData,
  // VALE-450 planning extraction / VALE-466 review surface
  PlanningReviewState,
  AiScribePlanningTestatorSlot,
  AiScribePlanningPersonRef,
  AiScribePlanningAppointment,
  AiScribePlanningDocumentStatus,
  AiScribePlanningWish,
  AiScribePlanningTestator,
  AiScribePlanningDependant,
  AiScribePlanningEntitlement,
  PlanningEntitlementDropReason,
  AiScribeDroppedPlanningEntitlement,
  // VALE-1763 [EPLAN-17]: the structural facts and asset ownership
  AiScribePlanningStructurePerson,
  AiScribePlanningCompanyInterest,
  AiScribePlanningCompany,
  AiScribePlanningTrustInterest,
  AiScribePlanningTrust,
  AiScribePlanningAssetOwner,
  // VALE-1839 [EPLAN-22]: the meeting kind, the conflicts, and the verbs
  AiScribePlanningRemovable,
  PlanningMeetingKind,
  AiScribePlanningConflict,
  PlanningFactVerb,
  PlanningChangeSetEntry,
  RecordingPlanningExtractedData,
  PlanningApplySkipReason,
  PlanningApplyOutcome,
  ConsolidationProgress,
  LawyerNotes,
  RecordingDetail,
  PaginationMeta,
  RecordingListResponse,
  RecordingListParams,
  FieldChange,
  PreviewContactDiff,
  PreviewInventoryItemDiff,
  PreviewChecklistItem,
  PreviewExistingItemNote,
  PreviewDeceasedAddress,
  PreviewDeceased,
  PreviewMatterDiff,
  PreviewChangesSummary,
  PreviewChangesResponse,
  PostalAddress,
  ProposedContact,
  InstitutionPreviewRef,
  ProposedInventoryItem,
  ApplyContact,
  ApplyInventoryItem,
  ApplyChecklistItem,
  ApplyExistingItemNote,
  ApplyDeceasedAddress,
  ApplyMatterDeceased,
  ApplyChangesRequest,
  ApplyChangesResponse,
  CreateRecordingRequest,
  UploadCompleteRequest,
  UpdateRecordingRequest,
  ClientTelemetry,
  ClientTelemetryAtUpload,
  StoredAudioChunk,
  StoredRecordingMeta,
  OrphanedRecording
} from './types';

// LF-3743: handoff types
export type {
  HandoffPhase,
  HandoffErrorCode,
  HandoffCreatedResponse,
  HandoffMobileSession,
  HandoffErrorResponse,
  HandoffClaimRequest,
  HandoffUploadCompleteRequest
} from './handoff-types';

// LF-3743: handoff copy
export { HANDOFF_COPY, copyForError } from './handoff-copy';

// LF-3743: desktop handoff client helpers (directApiClient-backed)
export { regenerateHandoff, cancelHandoff } from './handoff-client';

// LF-3835: top-level ambient QR handoff lifecycle hook + 409 helper
export {
  useScribeHandoff,
  isHandoffAlreadyClaimed
} from './use-scribe-handoff';
export type { UseScribeHandoffResult } from './use-scribe-handoff';

// LF-3743: mobile BFF helpers (used by the public /device-recording route)
export {
  getHandoffSession,
  claimHandoff,
  startHandoffRecording,
  completeHandoffUpload,
  discardHandoff,
  HandoffApiError
} from './handoff-mobile-api';

// Utilities
export {
  formatDuration,
  formatTimer,
  formatRecordingDate,
  getStatusConfig
} from './utils';

// Server actions
export {
  getRecordingsListAction,
  getRecordingDetailAction,
  getAudioUploadUrlAction,
  getRecordingAudioUploadUrlAction,
  createRecordingAction,
  startRecordingAction,
  discardCaptureAction,
  uploadCompleteAction,
  regenerateUploadUrlAction,
  applyChangesAction,
  consolidateRecordingAction,
  getConsolidationProgressAction,
  retryRecordingAction,
  reanalyseRecordingAction,
  updateRecordingAction,
  dismissRecordingAction,
  undismissRecordingAction,
  deleteRecordingAction,
  // VALE-480 note-template regeneration (summary only)
  regenerateRecordingSummaryAction
} from './actions';

// React Query hooks
export {
  useRecordings,
  useScribeRecordings,
  // LF-5995 lead-scoped capture — the only read that resolves a lead's recordings
  useLeadRecordings,
  useRecordingDetail,
  usePreviewChanges,
  useCreateRecording,
  useUpdateRecording,
  // VALE-1740 — the one save path behind both Summary surfaces
  useCommitSummarySection,
  // LF-3712 top-level Scribe session mutations (matter-agnostic)
  useUpdateScribeRecording,
  useEditScribeTranscript,
  useReanalyseScribeRecording,
  // VALE-480 — summary-only regeneration against a different note template
  useRegenerateRecordingSummary,
  useRetryScribeRecording,
  useDeleteScribeRecording,
  // LF-3743 handoff mutations
  useRegenerateHandoff,
  // LF-3712 FE-9 PDF export / VALE-480 DOCX export
  useDownloadRecordingPdf,
  useDownloadRecordingDocx
} from './hooks';

// LF-3712 FE-9 (PDF) + VALE-480 (DOCX): note exports
export {
  downloadRecordingPdf,
  downloadRecordingDocx,
  PdfServiceUnavailableError,
  type RecordingPdfScope,
  type RecordingDocxScope,
  type RecordingExportFormat
} from './download';

// LF-3712 FE-9: creator-scoped completion / failure toasts
export {
  RECORDING_PROCESSED_NOTIFICATION_TYPE,
  RECORDING_FAILED_NOTIFICATION_TYPE,
  notifyRecordingReady,
  notifyRecordingFailed
} from './recording-completion-toast';

// LF-3712 binding & override (create-matter, associate, unbind, overrides)
export type {
  MatterCandidate,
  MatterCandidateOverlap,
  ConfirmedDeceasedName,
  CreateMatterFromRecordingRequest,
  CreateMatterFromRecordingResponse,
  RecordingBindingErrorCode
} from './binding';
export { STRONG_OVERLAP_THRESHOLD, isStrongOverlap } from './binding';
export {
  recordingErrorCode,
  useMatterCandidates,
  useCreateMatterFromRecording,
  useAssociateRecording,
  useUnbindRecording,
  useTreatAsProbate,
  useDismissSuggestion,
  useRestoreSuggestion,
  useArchiveRecording,
  useUnarchiveRecording,
  useCancelRecording
} from './binding';

// IndexedDB
export {
  openDatabase,
  writeChunk,
  getChunksForRecording,
  deleteChunksForRecording,
  getOrphanedChunks,
  cleanupExpiredChunks,
  writeRecordingMeta,
  getRecordingMeta,
  deleteRecordingMeta
} from './indexed-db';
